// The manifest seal — the one thing that makes "the engine deploys the manifest
// it was handed" verifiable rather than hopeful.
//
// The launcher fetches the release's manifest, hashes THE BYTES AS SERVED, and
// passes that digest to the engine alongside the version. The engine fetches the
// same URL itself and refuses unless its bytes hash identically. Two independent
// fetches that must agree: mid-flight asset swap and transport corruption both
// become a refusal instead of a silently different install.
//
// WHY A SEPARATE FETCH FROM `fetchManifest`. That one does
// `JSON.stringify(await res.json())` — it re-encodes the document. For its
// callers (the update rail, which only wants the parsed object) that is
// harmless. Here it would be fatal: the engine hashes what `curl` wrote to
// disk, so a digest taken after a re-encode is a digest of a document nobody
// else will ever see. Key order and whitespace survive a byte copy and do not
// survive a round-trip through a parser.
//
// THE INVARIANT, for anyone editing this file: `raw` is never normalized,
// re-indented, re-ordered or trimmed between the wire and the hash. If you find
// yourself reaching for JSON.stringify on the way to `manifestDigestOf`, the
// seal is already broken.
//
// This digest is NOT a supply-chain signature. It cannot detect an asset that
// was already replaced before the launcher looked — only a signed manifest can,
// and that is the named follow-on (the schema's `signature` field is null on
// every release published so far). What it does close is real, and today
// NOTHING verifies the manifest on either rail.
import * as fs from "node:fs";
import { createHash } from "node:crypto";
import { channelUrlForVersion, CHANNEL_LATEST_URL } from "@m8t-stack/platform-release";
import { validateManifest, type ReleaseManifest } from "./release-manifest.js";
import { getGhToken } from "./gh-helpers.js";
import { LocalCliError } from "./errors.js";
import type { ManifestSource } from "./release-channel.js";

export interface SealedManifest {
  /** The bytes exactly as served. What the digest is over — never re-serialize before hashing. */
  raw: string;
  /** `sha256:<hex>` over `raw`, in the same shape a registry states a digest. */
  digest: string;
  /** The parsed, schema-validated document. Convenience only; `raw` is the authority. */
  manifest: ReleaseManifest;
}

/**
 * `sha256:<hex>` over exactly these bytes.
 *
 * Registry-digest shape on purpose: every other digest an operator meets in this
 * system (`gateway.digest`, the descriptor's image digests) reads the same way,
 * and one shape means one thing to compare by eye in a refusal message.
 */
export function manifestDigestOf(raw: string): string {
  return `sha256:${createHash("sha256").update(raw, "utf8").digest("hex")}`;
}

interface Deps {
  fetchImpl?: typeof fetch;
  ghToken?: () => Promise<string>;
  readFile?: (p: string) => string;
}

/**
 * Fetch a manifest and seal it: the served bytes, their digest, and the parsed
 * document. Validation still runs — a manifest that fails the schema is refused
 * here rather than handed to an engine that would refuse it later, further from
 * the operator who can fix it.
 */
export async function fetchSealedManifest(source: ManifestSource, deps: Deps = {}): Promise<SealedManifest> {
  const readFile = deps.readFile ?? ((p: string) => fs.readFileSync(p, "utf8"));
  const fetchImpl = deps.fetchImpl ?? fetch;
  const ghToken = deps.ghToken ?? getGhToken;

  let raw: string;
  if ("file" in source) {
    try {
      raw = readFile(source.file);
    } catch (e) {
      throw new LocalCliError({
        code: "PLATFORM_MANIFEST_READ_FAILED",
        message: `Could not read manifest file '${source.file}': ${(e as Error).message}`,
      });
    }
  } else {
    const url = "url" in source ? source.url : source.version ? channelUrlForVersion(source.version) : CHANNEL_LATEST_URL;
    // Best-effort auth, exactly as `fetchManifest` does it. The releases channel
    // is anonymously readable (verify-cut.sh asserts it, because an install holds
    // no credentials) — but a maintainer pointing this at a private mirror should
    // not have to think about it.
    let token: string | undefined;
    try { token = await ghToken(); } catch { token = undefined; }
    const res = await fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      throw new LocalCliError({
        code: "PLATFORM_MANIFEST_FETCH_FAILED",
        message: `GET ${url} returned HTTP ${res.status.toString()}.`,
        hint: "The engine fetches this same URL and must see the same bytes. Check the release exists and its manifest asset is published.",
      });
    }
    // `.text()`, never `.json()` — see the invariant at the top of this file.
    raw = await res.text();
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    // Reached by a 200 that returned a login page or an error document. Hashing
    // it would produce a perfectly stable digest of the wrong thing, and the
    // engine would agree with us about it.
    throw new LocalCliError({
      code: "PLATFORM_MANIFEST_PARSE_FAILED",
      message: `Manifest is not valid JSON: ${(e as Error).message}`,
    });
  }
  const errors = validateManifest(parsed);
  if (errors.length > 0) {
    throw new LocalCliError({
      code: "PLATFORM_MANIFEST_INVALID",
      message: `Fetched manifest failed validation:\n  - ${errors.join("\n  - ")}`,
    });
  }

  return { raw, digest: manifestDigestOf(raw), manifest: parsed as ReleaseManifest };
}
